/**
 * NewProcessCardForm (editable-workspace-processes, PR3) — "+ New card" on a
 * non-intake board. Title + optional owner + opening stage; creates a
 * kind='process_item' work_item on the selected process. No intake-specific
 * fields (referral / contact / client setup) — those are kind-fenced server-side.
 *
 * Field pack v1: when the board has configured card fields
 * (tenants.config.workspace.process_card_fields), the form adds the
 * config-labeled category picker (e.g. "Role") and/or date input (e.g.
 * "Application date"). Unconfigured boards render exactly as before.
 */

import { useState, type ReactNode } from 'react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import type { ProcessCardCategoryField } from '@/lib/processCardFields';
import type { CreateWorkItemInput, PipelineStageWire, WorkspaceMemberWire } from '@/lib/api';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

export interface NewProcessCardFormProps {
  processId: string;
  /** The board's ACTIVE stages (the opening-stage picker). */
  stages: PipelineStageWire[];
  members: WorkspaceMemberWire[];
  /** Field pack v1: the board's configured category field, or null. */
  categoryField?: ProcessCardCategoryField | null;
  /** Field pack v1: the board's configured due-date label, or null (no date input). */
  dueDateLabel?: string | null;
  submitting?: boolean;
  error?: string | null;
  onCancel: () => void;
  onCreate: (input: CreateWorkItemInput) => void;
}

export function NewProcessCardForm({ processId, stages, members, categoryField = null, dueDateLabel = null, submitting = false, error, onCancel, onCreate }: NewProcessCardFormProps): ReactNode {
  const ordered = [...stages].sort((a, b) => a.sort_order - b.sort_order);
  const [title, setTitle] = useState('');
  const [stageId, setStageId] = useState<string>(ordered[0]?.id ?? '');
  const [ownerUserId, setOwnerUserId] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');

  const canSubmit = title.trim().length > 0 && stageId !== '';

  return (
    <Dialog title="New card" testId="new-card-dialog" onClose={onCancel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          onCreate({
            kind: 'process_item',
            title: title.trim(),
            process_id: processId,
            stage_id: stageId,
            assignee_user_id: ownerUserId || null,
            ...(categoryField ? { category: category || null } : {}),
            ...(dueDateLabel ? { due_date: dueDate || null } : {}),
          });
        }}
      >
        <label className="block text-sm text-ink mb-3">
          Title
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="new-card-title"
            className="mt-1 w-full rounded-base border border-sage-200 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block text-sm text-ink mb-3">
          Stage
          <SelectField size="field"
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
            data-testid="new-card-stage"
            className="w-full mt-1"
          >
            {ordered.map((s) => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </SelectField>
        </label>
        {categoryField && (
          <label className="block text-sm text-ink mb-3">
            {categoryField.label} <span className="text-ink-soft">(optional)</span>
            <SelectField size="field"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="new-card-category"
              className="w-full mt-1"
            >
              <option value="">None</option>
              {categoryField.options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </SelectField>
          </label>
        )}
        {dueDateLabel && (
          <label className="block text-sm text-ink mb-3">
            {dueDateLabel} <span className="text-ink-soft">(optional)</span>
            <DateInput
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              data-testid="new-card-due-date"
              size="compact" className="mt-1 w-full"
            />
          </label>
        )}
        <label className="block text-sm text-ink mb-4">
          Owner <span className="text-ink-soft">(optional)</span>
          <SelectField size="field"
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
            data-testid="new-card-owner"
            className="w-full mt-1"
          >
            <option value="">Unassigned</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.name}</option>
            ))}
          </SelectField>
        </label>

        {error && <p className="text-xs text-pepper mb-3" role="alert" data-testid="new-card-error">{error}</p>}

        <div className="flex justify-end gap-2">
          <TextAction onClick={onCancel}>Cancel</TextAction>
          <Button size="sm" type="submit" disabled={submitting || !canSubmit} data-testid="new-card-submit">
            {submitting ? 'Adding…' : 'Add card'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
